// frontend/src/pages/CentrifugeLaserPage.jsx
import React, { useState, useRef, useEffect } from "react";
import { Canvas } from "@react-three/fiber";
import { OrbitControls } from "@react-three/drei";
import CentrifugeLaser3D from "../components/CentrifugeLaser3D";

export default function CentrifugeLaserPage() {
  const [entropyUI, setEntropyUI] = useState(null);
  const [entropyDyn, setEntropyDyn] = useState(null);

  // Dernier instantané de scène remonté par CentrifugeLaser3D. Un ref, pas un
  // état : il est réécrit à chaque frame et seul le POST le consomme.
  const snapshotRef = useRef({ positions: [], velocities: [] });
  const [loadingUI, setLoadingUI] = useState(true);

  const [running, setRunning] = useState(true);
  const [chaos, setChaos] = useState(false);
  const [glow, setGlow] = useState(true);

  const [spiralSpeedPct, setSpiralSpeedPct] = useState(50);
  const [ringSpeedPct, setRingSpeedPct] = useState(80);
  const [turboPct, setTurboPct] = useState(0); // 🔹 slider Turbo
  const [flashRatePct, setFlashRatePct] = useState(30);

  const [bloomIntensity, setBloomIntensity] = useState(1.2);
  const [bloomRadius, setBloomRadius] = useState(0.6);
  const [bloomThreshold, setBloomThreshold] = useState(0.0);

  const canvasRef = useRef(null);
  const orbitRef = useRef(null);

  // --- Entropie UI (backend) ---
  useEffect(() => {
    setLoadingUI(true);
    const controller = new AbortController();

    fetch("/api/geometry/centrifuge_laser/entropy", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        chaos,
        glow,
        spiral_speed: spiralSpeedPct / 100,
        ring_speed: ringSpeedPct / 100,
        turbo: turboPct / 100, // 🔹 envoyé au backend
        flash_rate: flashRatePct / 100,
        bloom_intensity: bloomIntensity,
        bloom_radius: bloomRadius,
        bloom_threshold: bloomThreshold,
      }),
      signal: controller.signal,
    })
      .then((r) => r.json())
      .then((data) => {
        if (typeof data.entropy === "number") setEntropyUI(data.entropy);
      })
      .catch(() => setEntropyUI(null))
      .finally(() => setLoadingUI(false));

    return () => controller.abort();
  }, [chaos, glow, spiralSpeedPct, ringSpeedPct, turboPct, flashRatePct, bloomIntensity, bloomRadius, bloomThreshold]);

  // --- Entropie Dynamique (backend) ---
  // `dyn_entropy` mesure un état PHYSIQUE (docs/DOCTRINE_ENTROPIE_PHYSIQUE.md).
  // On envoyait auparavant `rotation_speed`, `dispersion` et
  // `turbulence: chaos ? Math.random() : 0.2` : deux curseurs et un tirage
  // aléatoire du navigateur. Aucune position, aucune vitesse, aucune mesure.
  // On transmet désormais l'instantané réel de la scène affichée.
  useEffect(() => {
    const interval = setInterval(async () => {
      const snap = snapshotRef.current;
      if (!snap || !snap.positions.length) return;   // rien à mesurer encore
      try {
        const res = await fetch("/api/geometry/centrifuge_laser/dyn_entropy", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            positions: snap.positions,
            velocities: snap.velocities,
          }),
        });

        const data = await res.json();
        if (typeof data.entropy_dyn === "number") {
          setEntropyDyn(data.entropy_dyn);
        }
      } catch (err) {
        console.error("Erreur dyn_entropy:", err);
      }
    }, 2000); // toutes les 2 secondes

    return () => clearInterval(interval);
  }, [spiralSpeedPct, ringSpeedPct, chaos]);

  const entropyTotal =
    entropyUI !== null && entropyDyn !== null
      ? (entropyUI + entropyDyn).toFixed(4)
      : "--";

  return (
    <div
      className="page-container"
      style={{ display: "flex", flexDirection: "column", alignItems: "center", padding: "20px" }}
    >
      <h1>Centrifuge Laser Visualizer</h1>

      {/* Jauges */}
      <div style={{ display: "flex", gap: 36, marginBottom: 22 }}>
        <div className="card" style={{ minWidth: 170 }}>
          <h2>Entropie UI</h2>
          <div style={{ fontSize: "1.2rem", color: "#03dac6" }}>
            {loadingUI ? "⏳" : entropyUI !== null ? entropyUI.toFixed(4) : "❌"}
          </div>
        </div>
        <div className="card" style={{ minWidth: 170 }}>
          <h2 style={{ color: "#ff0266" }}>Entropie Dynamique</h2>
          <div style={{ fontSize: "1.2rem", color: "#ff0266" }}>
            {entropyDyn !== null ? entropyDyn.toFixed(4) : "⏳"}
          </div>
        </div>
        <div className="card" style={{ minWidth: 180 }}>
          <h2 style={{ color: "#bb86fc" }}>Score Total</h2>
          <div style={{ fontSize: "1.2rem", color: "#bb86fc" }}>{entropyTotal}</div>
        </div>
      </div>

      {/* Contrôles + Canvas */}
      <div style={{ display: "flex", alignItems: "center", gap: "28px" }}>
        {/* Contrôles */}
        <div style={{ display: "flex", flexDirection: "column", gap: "14px", minWidth: 260 }}>
          <button className="generate-button" onClick={() => setRunning((p) => !p)}>
            {running ? "⏸ Stop Animation" : "▶ Start Animation"}
          </button>

          <button
            onClick={() => setChaos((p) => !p)}
            style={{
              padding: "12px 18px",
              borderRadius: "6px",
              cursor: "pointer",
              backgroundColor: chaos ? "#ff0266" : "#03dac6",
              fontWeight: "bold",
            }}
          >
            {chaos ? "Désactiver Chaos" : "Activer Chaos"}
          </button>

          <button
            onClick={() => setGlow((p) => !p)}
            style={{
              padding: "12px 18px",
              borderRadius: "6px",
              cursor: "pointer",
              backgroundColor: glow ? "#ffd600" : "#666",
              fontWeight: "bold",
            }}
          >
            {glow ? "Désactiver Glow" : "Activer Glow"}
          </button>

          {/* Sliders */}
          <div className="input-group">
            <label>Vitesse centrifuge : {spiralSpeedPct}%</label>
            <input type="range" min={0} max={100} value={spiralSpeedPct} onChange={(e) => setSpiralSpeedPct(Number(e.target.value))} />
          </div>

          <div className="input-group">
            <label>Vitesse Anneau : {ringSpeedPct}%</label>
            <input type="range" min={0} max={150} value={ringSpeedPct} onChange={(e) => setRingSpeedPct(Number(e.target.value))} />
          </div>

          {/* 🔹 Slider Turbo */}
          <div className="input-group">
            <label>Turbo : {turboPct}%</label>
            <input
              type="range"
              min={0}
              max={200}
              value={turboPct}
              onChange={(e) => setTurboPct(Number(e.target.value))}
            />
          </div>

          <div className="input-group">
            <label>Flash : {flashRatePct}%</label>
            <input type="range" min={0} max={100} value={flashRatePct} onChange={(e) => setFlashRatePct(Number(e.target.value))} />
          </div>

          <div className="input-group">
            <label>Bloom Intensity : {bloomIntensity.toFixed(2)}</label>
            <input type="range" min={0} max={3} step={0.1} value={bloomIntensity} onChange={(e) => setBloomIntensity(Number(e.target.value))} />
          </div>

          <div className="input-group">
            <label>Bloom Radius : {bloomRadius.toFixed(2)}</label>
            <input type="range" min={0} max={2} step={0.1} value={bloomRadius} onChange={(e) => setBloomRadius(Number(e.target.value))} />
          </div>

          <div className="input-group">
            <label>Bloom Threshold : {bloomThreshold.toFixed(2)}</label>
            <input type="range" min={0} max={1} step={0.01} value={bloomThreshold} onChange={(e) => setBloomThreshold(Number(e.target.value))} />
          </div>
        </div>

        {/* Canvas */}
        <div className="card" style={{ width: 600, height: 480, background: "#181818" }}>
          <Canvas ref={canvasRef} camera={{ position: [0, 5, 25], fov: 60 }} style={{ width: 600, height: 480, borderRadius: "10px" }}>
            <ambientLight intensity={0.7} />
            <pointLight position={[30, 40, 30]} intensity={1.2} />
            <CentrifugeLaser3D
              running={running}
              chaos={chaos}
              glow={glow}
              spiralSpeedPct={spiralSpeedPct}
              ringSpeedPct={ringSpeedPct}
              turboPct={turboPct}   // 🔹 branché
              flashRatePct={flashRatePct}
              bloomIntensity={bloomIntensity}
              bloomRadius={bloomRadius}
              bloomThreshold={bloomThreshold}
              onSnapshot={(s) => {
                snapshotRef.current = s;
              }}
              onEntropyUpdate={(val) => setEntropyDyn(val)}
            />
            <OrbitControls ref={orbitRef} enableZoom={true} enablePan={true} minDistance={5} maxDistance={100} target={[0, 0, 0]} />
          </Canvas>
        </div>
      </div>
    </div>
  );
}
